import { Domain } from 'library/ts/model/enums/web/Domain';
import { AbstractPauseAdUtilities } from 'tag/ts/tag/ads/pause/AbstractPauseAdUtilities';
import { FreeWheelPauseAdModel } from 'tag/ts/tag/model/ads/pause/FreeWheelPauseAdModel';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';
import { PauseAdType } from 'tag/ts/tag/model/type/ads/PauseAdType';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { FWUtilities } from 'tag/ts/tag/utilities/FWUtilities';
export class FreeWheelPauseAdUtilities extends AbstractPauseAdUtilities {
    #fwModel;
    #excludedFMSKeys = [
        'description_url',
        '_fw_continuous_play',
        '_fw_h_referer',
        'is_lat',
        'sz',
        'cpPre',
        'cpSession',
        'dai-excl',
        'api-key',
        'rdid',
        'idtype'
    ];
    constructor(argument) {
        super({ tag: argument.tag, name: 'FreeWheelPauseAdUtilities', type: PauseAdType.FREEWHEEL });
        this.#fwModel = new FreeWheelPauseAdModel({ tag: this?.tag, embedType: this.#getSystemEmbedType() });
    }
    #isDesktop = () => !!window?.SmartTag?.capabilities?.isDesktop;
    #isPPlus = () => !!this?.runtimeVO?.top?.parsed?.host?.includes?.(Domain.PPLUS);
    #isLive = () => !!this?.tag?.model?.contentUtilities?.isLive?.(this.params, this.apiMetadata);
    #getSystemDevice = () => this?.params?.SYSTEM?.device;
    #getSystemEmbedType = () => this?.params?.SYSTEM?.embedType;
    #getBusinessDivision = () => (this.#isPPlus() ? 'streaming' : 'vcbs');
    #getPartner = () => (this.#isPPlus() ? 'paramountplus' : 'cbs');
    #getCountryCode = () => this?.tag?.country?.toString();
    #getPlatform = () => (this.#isDesktop() ? 'desktop' : 'mobile');
    #getStreamType = () => (this.#isLive() ? 'live' : 'vod');
    #getVendorCode = () => this?.tag?.params?.CONTEXT?.metadata?.vendorCode || '';
    getAdCallURL = () => {
        const constants = FreewheelConstants.getInstance();
        const fwUtil = FWUtilities.getInstance();
        const prefix = 'imafw_';
        const cleanKey = (key) => {
            return (key.substring(0, prefix.length) === prefix) ? key.substring(prefix.length) : key;
        };
        const createQueryString = (object) => Array.from(new Set(Object.keys(object)
            .filter((key) => !this.utils.Type.isEmpty(object[key]))
            .filter((key) => !this.#excludedFMSKeys.includes(key) && !this.#excludedFMSKeys.includes(key.substring(prefix.length)))
            .map((key) => {
            if (this.utils.Type.isArray(object[key]) && object[key].length > 0) {
                return object[key]
                    .filter((item) => !this.utils.Type.isEmpty(item))
                    .map((item) => `${cleanKey(key)}=${fwUtil.transformToUrlValue(item)}`)
                    .join('&');
            }
            if (this.utils.Type.isString(object[key]) ||
                this.utils.Type.isNumber(object[key]) ||
                this.utils.Type.isBoolean(object[key])) {
                return `${cleanKey(key)}=${fwUtil.transformToUrlValue(object[key])}`;
            }
        })))
            .join('&');
        const scheme = constants.scheme;
        const host = constants.hostIMA;
        const paramsQs = [
            createQueryString(this.#fwModel?.globalParams || {}),
            createQueryString(this.#fwModel?.mergedParams || {}),
            createQueryString(this.#fwModel?.slotParams || {})
        ].join(';');
        const url = `${scheme}://${host}?${paramsQs}`;
        this.logger?.log('getAdCallURL:', url);
        return url;
    };
    cleanUpTextResponse = (xmlString) => {
        xmlString = this.utils.String.cleanUp(xmlString);
        xmlString = xmlString.replace(/<Impression><!\[CDATA\[]><\/Impression>/g, '');
        return xmlString;
    };
    processResponse = (xml) => {
        let imageSource = undefined;
        let clickThrough = undefined;
        let clickTrackingURLs = undefined;
        const rootNode = xml.querySelector('Creative')
            ||
                xml.querySelector('creative');
        if (this.utils.Type.isElement(rootNode)) {
            const image = rootNode.querySelector('MediaFile[type*="image/"]')
                ||
                    rootNode.querySelector('mediafile[type*="image/"]');
            imageSource = image && image.textContent ? image.textContent.trim() : undefined;
            const clickURL = rootNode.querySelector('ClickThrough')
                ||
                    rootNode.querySelector('clickthrough');
            clickThrough = clickURL && clickURL.textContent ? clickURL.textContent.trim() : undefined;
            clickTrackingURLs = this.getClickTrackingURLs(rootNode);
        }
        const results = {
            imageSource: imageSource,
            clickThrough: clickThrough,
            clickTrackingURLs: clickTrackingURLs,
            impression: this.impression
        };
        this?.logger?.log('processResponse: IPauseAdResults:', results);
        return results;
    };
    getClickTrackingURLs = (node) => {
        const clickTrackingURLs = new Array();
        const clickTrackingNodes = node.querySelectorAll('ClickTracking');
        clickTrackingNodes.forEach((node) => {
            const content = (node?.firstChild?.textContent) ? node.firstChild.textContent.trim() : '';
            if (!this.utils.Type.isEmpty(content))
                clickTrackingURLs.push(content);
        });
        return clickTrackingURLs;
    };
    createSiteSectionId = (delimiter = '_') => {
        switch (this.#getSystemEmbedType()) {
            case EmbedType.DESKTOP:
            case EmbedType.MOBILE_WEB:
                return this.createSiteSectionIdWeb(delimiter);
            case EmbedType.HTML5:
                return this.createSiteSectionIdForHtml5();
            default:
                this?.logger?.warn('createSiteSectionId', 'uknown EmbedType... default to creating site section ID for web', 'EmbedType:', this.#getSystemEmbedType());
                return this.createSiteSectionIdWeb(delimiter);
        }
    };
    createSiteSectionIdWeb = (delimiter = '_') => {
        return [
            this.#getBusinessDivision(),
            this.#getPartner(),
            this.#getCountryCode(),
            this.#getPlatform(),
            'web',
            this.#getStreamType(),
            this.#getVendorCode()
        ].join(delimiter);
    };
    createSiteSectionIdForHtml5 = (delimiter = '_') => {
        return [
            this.#getBusinessDivision(),
            this.#getPartner(),
            this.#getCountryCode(),
            'ctv',
            (this.#getSystemDevice()?.toLowerCase() || 'unknown'),
            this.#getStreamType()
        ].join(delimiter);
    };
}
